import React from 'react';
import {
    i18nFn,
    useAppSettings,
    useAuthPin,
    useCreateMemberPassword,
    useSpringBigStore ,
} from '@springbig/shared';
import { VStack, FormControl, WarningIcon } from 'native-base';
import { Caption, H3 } from '~/shared/Typography';
import * as yup from 'yup';
import { useController, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AppRouteProps, useAppNavigate } from '~/pages';
import { Input, Button } from '~/shared/Theme';
import { NavigateOptions, Params } from 'react-router';

type CreateStepProps = {
    t: i18nFn;
    onEvent: (message: string, value?: boolean) => void;
    navigate: (route: AppRouteProps, opts?: NavigateOptions) => void;
    params: Params<string>;
};

export const CreateStep = ({
    t,
    onEvent,
    navigate,
    params,
}: CreateStepProps) => {


    if (!navigate) {
        navigate = useAppNavigate().navigate;
    }

    if (!params) {
        params = useAppNavigate().params;
    }

    const { defaultScreen } = useAppSettings();

    const {
        mutate: createPassword,
        isLoading: isLoadingCode
    } = useCreateMemberPassword();
    const { mutate: authPin } = useAuthPin();

    const codeSize = 4;

    const schemaCode = yup.object().shape({
        code: yup
            .string()
            .required('code is required')
            .matches(new RegExp(`^.{${codeSize}}$`), 'invalid code format')
            .trim(),
        confirm_code: yup
            .string()
            .required('confirming code is required')
            .matches(new RegExp(`^.{${codeSize}}$`), 'invalid code format')
            .oneOf([yup.ref('code'), null], 'codes must match')
            .trim()
    });

    type FormDataCode = yup.InferType<typeof schemaCode>;

    const { control: controlCode, handleSubmit: handleSubmitCode, formState: { errors: errorsCode }, watch } = useForm({
        resolver: yupResolver(schemaCode)
    });

    const { field: codeField } = useController({
        control: controlCode,
        defaultValue: '',
        name: 'code',
    });

    const { field: confirmCodeField } = useController({
        control: controlCode,
        defaultValue: '',
        name: 'confirm_code',
    });
    const {
        setMobileSubscriberId,
        setWalletId,
        setPhoneNumber ,
    } = useSpringBigStore();

    const onSubmitCode = (data: FormDataCode): void => {

        createPassword({
            walletId: params.wallet_id,
            member: {
                password: data.code,
            }
        }, {
            onSuccess: (_) => {

                let target;
                // const messageTarget = stashboardTarget ? `messages/${stashboardTarget}` : defaultScreen;
                // let target = `w/${params.wallet_id}/${(messageTarget || 'messages')}`;

                const plan_id = localStorage.getItem('plan_id');
                if (plan_id) {
                    localStorage.removeItem('plan_id');
                    target = `w/${params.wallet_id}/subscription/${plan_id}`;
                }

                const link_id = localStorage.getItem('link_id');
                if (link_id) {
                    localStorage.removeItem('link_id');
                    target = `w/${params.wallet_id}/subscriptions`;
                }

                authPin({
                    pin: btoa(data.code),
                    stashboardOrMemberKey: params.wallet_id,
                    reference: 'member'
                }, {
                    onSuccess: (response) => {

                        if (response) {
                            setWalletId(response.merchant_member_key);
                            setPhoneNumber(response.phone_number);
                            setMobileSubscriberId(response.mobile_subscriber);
                            if (target) {
                                navigate(target as AppRouteProps);
                            }
                            else {
                                onEvent('messages', response.passkey_enabled);
                            }
                        }
                    }
                });
            },
        });
    };

    const codeValue = watch('code');
    const confirmCodeValue = watch('confirm_code');

    return (
        <VStack p={8} space={8} w={'100%'} alignItems={'center'}>
            <VStack>
                <H3 fontSize={20} text={'auth.ten_dlc.create.title'} />
                <Caption text={'auth.ten_dlc.create.description'} />
            </VStack>
            <VStack space={4} w={'100%'}>
                <FormControl isInvalid={!!errorsCode.code} w={'100%'}>
                    <VStack space={4}>
                        <VStack>
                            <FormControl.Label>
                                <Caption text={'auth.ten_dlc.create.create_pin'} />
                            </FormControl.Label>
                            <Input
                                {...codeField}
                                isInvalid={!!errorsCode.code}
                                isDisabled={isLoadingCode}
                                maxLength={codeSize}
                                returnKeyType={'done'}
                            />
                        </VStack>
                        <VStack>
                            <FormControl.Label>
                                <Caption text={'auth.ten_dlc.create.confirm_pin'} />
                            </FormControl.Label>
                            <Input
                                {...confirmCodeField}
                                isInvalid={!!errorsCode.code}
                                isDisabled={isLoadingCode}
                                maxLength={codeSize}
                                returnKeyType={'done'}
                            />
                        </VStack>
                        { errorsCode && errorsCode.code &&
                            <FormControl.ErrorMessage
                                leftIcon={<WarningIcon size={3} />}
                            >
                                { errorsCode.code?.message }
                            </FormControl.ErrorMessage>
                        }
                        { errorsCode && errorsCode.confirm_code &&
                            <FormControl.ErrorMessage
                                leftIcon={<WarningIcon size={3} />}
                            >
                                { errorsCode.confirm_code?.message }
                            </FormControl.ErrorMessage>
                        }
                        { (codeValue && codeValue.length === codeSize && confirmCodeValue && confirmCodeValue.length === codeSize && codeValue !== confirmCodeValue) && (
                            <Caption color={'red'}>
                                code must match
                            </Caption>
                        )}
                    </VStack>
                </FormControl>
                <Button
                    text={'auth.ten_dlc.create.submit_pin'}
                    onPress={handleSubmitCode(onSubmitCode)}
                    loading={isLoadingCode}
                />
            </VStack>
        </VStack>
    );
};
